// adapted from: https://github.com/WizardComputer/stimulus-multiselect

const activeSelector = "[aria-selected='true']"

import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'list',
    'search',
    'preview',
    'dropdown',
    'item',
    'addable',
    'inputContainer',
    'container',
  ]

  static values = {
    items: { type: Array, default: [] },
    selected: { type: Array, default: [] },
    searchUrl: String,
    searchRemote: { type: Boolean, default: false },
    preloadUrl: String,
    addableUrl: String,
    disabled: { type: Boolean, default: false },
    focusedClass: { type: String, default: '' },
    unfocusedClass: { type: String, default: '' },
  }

  connect() {
    // this used to be a stimulus "target", hence the name, but is now just a normal var.
    // reason being this lets render the <select> on the rails side without having to specify a target attribute on it
    this.hiddenTarget = this.element.querySelector('select[multiple]')
    if (!this.hiddenTarget) {
      throw new Error("A <select> element with 'multiple' enabled is required")
    }

    // initial render of list items
    this.listTarget.innerHTML = this.allItems

    this.search = debounce(this.search.bind(this), 300)
    this.enhanceHiddenSelect()
    if (this.preloadUrlValue) this.preload()

    this.searchTarget.addEventListener('focus', () => {
      this.showFocus(true)
    })
    this.searchTarget.addEventListener('blur', () => {
      this.showFocus(false)
    })

    // use flowbite to implement the dropdown menu
    this.dropdown = new Dropdown(this.dropdownTarget, this.inputContainerTarget, {
      triggerType: 'none',
      placement: 'bottom-start',
    })

    // populate the options from the hidden <select>
    this.triggerChangeEvents = false
    const items = []
    for (const option of this.hiddenTarget.options) {
      items.push({ text: option.label, value: option.value })
    }
    this.itemsValue = items

    const selectedItems = []
    for (const option of this.hiddenTarget.selectedOptions) {
      selectedItems.push({ text: option.label, value: option.value })
    }
    this.selectedValue = selectedItems

    // don't allow "change" events to trigger until the the call to "this.selectedValue=" has
    // finished running its callbacks.
    // this avoids triggering the events during initial population of the options
    setTimeout(() => {
      this.triggerChangeEvents = true
    })
  }

  // Allows selecting the hidden select field from html and extracting selected id values:
  // document.getElementById("selectId").values - [2, 4, 23]
  enhanceHiddenSelect() {
    Object.defineProperty(this.hiddenTarget, 'values', {
      get: () => {
        if (this.selectedValue.length <= 0) return []

        return this.selectedValue.map((item) => item.value)
      },
    })
  }

  search() {
    if (this.searchRemoteValue) return this.searchRemote()

    this.searchLocal()
  }

  async searchRemote() {
    if (this.searchTarget.value === '') return

    const response = await fetch(
      this.searchUrlValue +
        '?' +
        new URLSearchParams({
          q: this.searchTarget.value,
          preselects: this.selectedValue.map((x) => x.value).join(','),
        })
    )

    const searchedItems = await response.json()

    this.itemsValue = searchedItems
    this.showDropdown(true)
  }

  searchLocal() {
    this.showDropdown(true)
    if (this.searchTarget.value === '') {
      const theRestOfTheItems = this.itemsValue.filter(
        (x) => !this.selectedValue.map((y) => y.value).includes(x.value)
      )
      this.listTarget.innerHTML = this.selectedItems
      this.listTarget.insertAdjacentHTML('beforeend', this.items(theRestOfTheItems))
    }

    let searched = this.itemsValue.filter((item) => {
      return item.text.toLowerCase().includes(this.searchTarget.value.toLowerCase())
    })

    const selectedSearched = this.selectedValue.filter((item) => {
      return item.text.toLowerCase().includes(this.searchTarget.value.toLowerCase())
    })

    searched = searched.filter((x) => !selectedSearched.map((y) => y.value).includes(x.value))

    if (searched.length === 0 && this.addableUrlValue) {
      this.listTarget.innerHTML = this.noResultsTemplate
      return this.noResultsTemplate
    }

    if (searched.length === 0) this.showDropdown(false)
    this.listTarget.innerHTML = this.items(selectedSearched, true)
    this.listTarget.insertAdjacentHTML('beforeend', this.items(searched))
  }

  async preload() {
    const response = await fetch(this.preloadUrlValue)

    const items = await response.json()
    this.itemsValue = items
  }

  showDropdown(visible) {
    visible ? this.dropdown.show() : this.dropdown.hide()
  }

  showFocus(enabled) {
    this.containerTarget.classList.toggle(this.unfocusedClassValue, !enabled)
    this.containerTarget.classList.toggle(this.focusedClassValue, enabled)
  }

  toggleDropdown() {
    if (this.dropdown.isVisible()) {
      this.showDropdown(false)
      if (this.selectedValue.length > 0) this.inputContainerTarget.style.display = 'none'
      this.searchTarget.blur()
    } else {
      if (this.itemsValue.length) this.showDropdown(true)
      this.searchTarget.focus()
    }
  }

  closeOnClickOutside({ target }) {
    if (this.element.contains(target)) return

    this.showDropdown(false)
    if (this.selectedValue.length > 0) this.inputContainerTarget.style.display = 'none'
    this.searchTarget.value = ''
    if (!this.searchRemoteValue) {
      this.listTarget.innerHTML = this.allItems
      this.selectedValue.forEach((selected) => {
        this.checkItem(selected.value)
      })
    }
  }

  searchUrlValueChanged() {
    if (this.searchUrlValue) this.searchRemoteValue = true
  }

  itemsValueChanged() {
    if (!this.hasListTarget) return

    if (this.itemsValue.length) {
      this.listTarget.innerHTML = this.items(this.itemsValue)
    } else {
      this.listTarget.innerHTML = this.noResultsTemplate
    }
  }

  selectedValueChanged() {
    if (!this.hasPreviewTarget || !this.hiddenTarget) return

    while (this.hiddenTarget.options.length) this.hiddenTarget.remove(0)

    if (this.selectedValue.length > 0) {
      this.previewTarget.innerHTML = this.pills
      // this.searchTarget.style.paddingTop = "0.5rem"

      this.selectedValue.forEach((selected) => {
        const option = document.createElement('option')
        option.text = selected.text
        option.value = selected.value
        option.setAttribute('selected', true)
        this.hiddenTarget.add(option)
      })

      if (!this.searchRemoteValue) {
        this.selectedValue.forEach((selected) => {
          this.checkItem(selected.value)
        })
      }
    } else {
      // this.searchTarget.style.paddingTop = "0"
      this.inputContainerTarget.style.display = ''
      this.previewTarget.innerHTML = ''
    }

    // make the internal <select> publish change events as it normally would without this stimulus controller
    if (this.triggerChangeEvents) {
      const eventOptions = { bubbles: true }
      this.hiddenTarget.dispatchEvent(new Event('change', eventOptions))
      this.hiddenTarget.dispatchEvent(new Event('input', eventOptions))
    }
  }

  removeItem(e) {
    e.stopPropagation()

    const itemToRemove = e.currentTarget.parentNode

    this.selectedValue = this.selectedValue.filter(
      (x) => x.value.toString() !== itemToRemove.dataset.value
    )
    this.uncheckItem(itemToRemove.dataset.value)
    this.element.dispatchEvent(
      new CustomEvent('multiselect-removed', { detail: { id: itemToRemove.dataset.value } })
    )
  }

  uncheckItem(value) {
    const itemToUncheck = this.listTarget.querySelector(`input[data-value="${value}"]`)

    if (itemToUncheck) itemToUncheck.checked = false
  }

  checkItem(value) {
    const itemToCheck = this.listTarget.querySelector(`input[data-value="${value}"]`)

    if (itemToCheck) itemToCheck.checked = true
  }

  toggleItem(input) {
    const item = {
      text: input.dataset.text,
      value: input.dataset.value,
    }
    let newSelectedArray = this.selectedValue

    if (input.checked) {
      newSelectedArray.push(item)

      if (this.focusedItem) {
        this.focusedItem.closest('li').classList.remove('multiselect__focused')
        this.focusedItem.removeAttribute('aria-selected')
      }

      input.setAttribute('aria-selected', 'true')
      input.closest('li').classList.add('multiselect__focused')
      this.element.dispatchEvent(new CustomEvent('multiselect-added', { detail: { item: item } }))
    } else {
      newSelectedArray = newSelectedArray.filter(
        (selected) => selected.value.toString() !== item.value
      )
      this.element.dispatchEvent(
        new CustomEvent('multiselect-removed', { detail: { id: item.value } })
      )
    }

    this.selectedValue = newSelectedArray
  }

  onKeyDown(e) {
    const handler = this[`on${e.key}Keydown`]
    if (handler) handler(e)
  }

  onArrowDownKeydown = (event) => {
    const item = this.sibling(true)
    if (item) this.navigate(item)
    event.preventDefault()
  }

  onArrowUpKeydown = (event) => {
    const item = this.sibling(false)
    if (item) this.navigate(item)
    event.preventDefault()
  }

  onBackspaceKeydown = () => {
    if (this.searchTarget.value !== '') return
    if (!this.selectedValue.length) return

    const selected = this.selectedValue
    const value = selected.pop().value

    this.uncheckItem(value)
    this.selectedValue = selected
    this.element.dispatchEvent(new CustomEvent('multiselect-removed', { detail: { id: value } }))
  }

  onEnterKeydown = (_e) => {
    if (this.focusedItem) this.focusedItem.click()
  }

  onEscapeKeydown = () => {
    if (this.searchTarget.value !== '') {
      this.searchTarget.value = ''
      return this.search()
    }

    this.toggleDropdown()
  }

  sibling(next) {
    const options = this.itemTargets
    const selected = this.focusedItem
    const index = options.indexOf(selected)
    const sibling = next ? options[index + 1] : options[index - 1]
    const def = next ? options[0] : options[options.length - 1]
    return sibling || def
  }

  async addable(e) {
    e.preventDefault()
    const query = this.searchTarget.value

    if (query === '' || this.itemsValue.some((item) => item.text === query)) return

    const response = await fetch(this.addableUrlValue, {
      method: 'POST',
      body: JSON.stringify({ addable: query }),
    })
    if (response.ok) {
      const addedItem = await response.json()

      this.addAddableItem(addedItem)
    }
  }

  addAddableItem(addedItem) {
    this.itemsValue = this.itemsValue.concat(addedItem)
    this.selectedValue = this.selectedValue.concat(addedItem)
    this.searchTarget.value = ''
    this.element.dispatchEvent(
      new CustomEvent('multiselect-added', { detail: { item: addedItem } })
    )
  }

  navigate(target) {
    const previouslySelected = this.focusedItem
    if (previouslySelected) {
      previouslySelected.removeAttribute('aria-selected')
      previouslySelected.closest('li').classList.remove('multiselect__focused')
    }

    target.setAttribute('aria-selected', 'true')
    target.closest('li').classList.add('multiselect__focused')
    target.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  get focusedItem() {
    return this.listTarget.querySelector(activeSelector)
  }

  focusSearch() {
    this.inputContainerTarget.style.display = ''
    this.searchTarget.focus()
  }

  addableEvent() {
    document.dispatchEvent(new CustomEvent('multiselect-addable'))
  }

  get noResultsTemplate() {
    if (!this.addableUrlValue) return `<div class="">${this.element.dataset.noResultsMessage}</div>`
    return `
      <div class="">
        <span class="" data-action="click->multiselect#addableEvent">
          ${this.element.dataset.addablePlaceholder}
        </span>
      </div>
    `
  }

  items(items, selected = false) {
    const checked = selected ? 'checked' : ''
    let itemsTemplate = ''

    items.forEach((item) => {
      itemsTemplate += this.itemTemplate(item, checked)
    })

    return itemsTemplate
  }

  get pills() {
    let itemsTemplate = ''

    this.selectedValue.forEach((item) => {
      itemsTemplate += this.pillTemplate(item)
    })

    return itemsTemplate
  }

  get selectedItems() {
    return this.items(this.selectedValue, true)
  }

  get allItems() {
    return this.items(this.itemsValue)
  }

  itemTemplate(item, selected = '') {
    return `
      <li class="hover:bg-sand">
        <label class="flex items-center py-1 px-4 font-medium">
          <input type="checkbox" ${selected} data-value="${item.value}" data-text="${item.text}" class="text-forest rounded focus:ring-lavender mr-2"
          data-action="multiselect#checkBoxChange" data-multiselect-target="item" tabindex="-1">
          <span>${item.text}</span>
        </label>
      </li>
    `
  }

  checkBoxChange(event) {
    event.preventDefault()
    this.searchTarget.focus()
    this.toggleItem(event.currentTarget)
  }

  pillTemplate(item) {
    const deleteButton = `
      <span class="p-1 ml-1 hover:cursor-pointer" data-action="click->multiselect#removeItem">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30" width="12px" height="12px">
          <path d="M25.707,6.293c-0.195-0.195-1.805-1.805-2-2c-0.391-0.391-1.024-0.391-1.414,0c-0.195,0.195-17.805,17.805-18,18c-0.391,0.391-0.391,1.024,0,1.414c0.279,0.279,1.721,1.721,2,2c0.391,0.391,1.024,0.391,1.414,0c0.195-0.195,17.805-17.805,18-18C26.098,7.317,26.098,6.683,25.707,6.293z"/>
          <path d="M23.707,25.707c0.195-0.195,1.805-1.805,2-2c0.391-0.391,0.391-1.024,0-1.414c-0.195-0.195-17.805-17.805-18-18c-0.391-0.391-1.024-0.391-1.414,0c-0.279,0.279-1.721,1.721-2,2c-0.391,0.391-0.391,1.024,0,1.414c0.195,0.195,17.805,17.805,18,18C22.683,26.098,23.317,26.098,23.707,25.707z"/>
        </svg>
      </span>
    `

    return `<div class="flex px-1 py-1 rounded-md border bg-white-dark text-charcoal" data-value="${item.value}" title="${item.text}">
      <span class="multiselect__pill-text ml-1 text-ellipsis whitespace-nowrap overflow-hidden">${item.text}</span>
      ${this.disabledValue ? null : deleteButton}
    </div>`
  }
}

function debounce(fn, delay) {
  let timeoutId = null

  return (...args) => {
    const callback = () => fn.apply(this, args)
    clearTimeout(timeoutId)
    timeoutId = setTimeout(callback, delay)
  }
}
